import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import type { StateCreator } from 'zustand';
import localForage from 'localforage';

import { DEFAULT_AMOUNT } from '@/constants';
import type { MessageType } from '@/types';

const storeName = import.meta.env.VITE_ZUSTAND_STORE_NAME || 'app-storage';

localForage.config({
  driver: localForage.LOCALSTORAGE,
});

// ─── State shape ────────────────────────────────────────────────────────────

interface AppState {
  _hasHydrated: boolean;
  _storageError: string | null;
  // Gift invite
  giftInviteCode: string;
  // Contribution
  contributionId: string;
  amount: number;
  privateAmount: boolean;

  // Gift details
  giftName: string;
  giftImageUrl: string;
  /** When the signed photo URL stops working, in epoch ms. */
  giftImageExpiresAt?: number;
  childFirstName: string;
  showChildPhoto: boolean;
  occasion: string;
  // Payment
  openBankingId: string;
  /** The gift reached the confirmation screen; survives a reload (GIFTWEB-30 AC 1). */
  giftCompleted?: boolean;
  goalType: string;
  messageType: MessageType;
  messageData: string;
  messageDurationSec?: number;
  messageSaved?: boolean;

  // details
  legalFirstName?: string;
  legalLastName?: string;
  email?: string;
  phone?: string;
  /** ISO 3166-1 alpha-2 of the mobile number's country (GIFTWEB-17). */
  phoneCountry?: string;
  relationshipToChild?: string;
  nameKnownAs?: string;
  contributorImageURL?: string;

  openBankingURL?: string;
}

// ─── Actions ────────────────────────────────────────────────────────────────

interface AppActions {
  setHasHydrated: (value: boolean) => void;
  setStorageError: (error: string | null) => void;
  resetStore: (hydrated?: boolean) => void;
  // Gift invite
  setGiftInviteCode: (val: string) => void;
  // Contribution
  setContributionId: (val: string) => void;
  setAmount: (amount: number) => void;
  setPrivateAmount: (privateAmount: boolean) => void;
  // Gift details
  setGiftName: (val: string) => void;
  setGiftImageUrl: (val: string) => void;
  /** Stores the signed photo URL with the moment it expires. */
  setGiftImage: (url: string, expiresInSeconds?: number) => void;
  setChildFirstName: (val: string) => void;
  setShowChildPhoto: (val: boolean) => void;
  setOccasion: (val: string) => void;
  setGoalType: (val: string) => void;
  setMessageType: (val: MessageType) => void;
  setMessageData: (val: string) => void;
  setMessageDurationSec: (val: number) => void;
  setMessageSaved: (val: boolean) => void;
  // details
  setLegalFirstName: (val: string) => void;
  setLegalLastName: (val: string) => void;
  setEmail: (val: string) => void;
  setPhone: (val: string) => void;
  setRelationshipToChild: (val: string) => void;
  setNameKnownAs: (val: string) => void;
  setContributorImageURL: (val: string) => void;
  /** Batched write used for draft-sync and post-submit save on the details step. */
  setContributorDetails: (details: Partial<ContributorDetails>) => void;

  // Payment
  setOpenBankingId: (val: string) => void;
  setOpenBankingURL: (val: string) => void;
  /** Forgets the payment attempt, keeping the contribution and gift context. */
  clearPaymentAttempt: () => void;
  /** Records a settled gift, and releases the link so it can be used again. */
  completeGift: () => void;
}

export type ContributorDetails = Pick<
  AppState,
  | 'legalFirstName'
  | 'legalLastName'
  | 'email'
  | 'phone'
  | 'phoneCountry'
  | 'relationshipToChild'
  | 'nameKnownAs'
>;

export type AppStore = AppState & AppActions;

// Initial values
const initialState: AppState = {
  _hasHydrated: false,
  _storageError: null,

  // Homepage
  giftInviteCode: '',
  amount: DEFAULT_AMOUNT,
  privateAmount: false,
  giftName: '',
  goalType: '',
  giftImageUrl: '',
  giftImageExpiresAt: 0,
  childFirstName: '',
  showChildPhoto: false,

  contributionId: '',

  // Personalise occasion
  occasion: '',
  messageType: '',
  messageData: '',
  messageDurationSec: 0,
  messageSaved: false,

  // Gift details
  legalFirstName: '',
  legalLastName: '',
  email: '',
  phone: '',
  phoneCountry: 'GB',
  relationshipToChild: '',
  nameKnownAs: '',
  contributorImageURL: '',

  openBankingId: '',
  giftCompleted: false,
  openBankingURL: '',
};

// Slice
/*
IF ACTIONS ARE SYNC AND DO ANY OF THE FOLLOWING, PUT IN A TRY...CATCH:
However there are cases where you'd want try/catch on synchronous actions too:

* If the action does any data transformation that could throw (e.g. JSON parsing, regex, date manipulation)
* If the action calls any external utility that might throw
* If you're doing any validation that throws on invalid input


IF ACTIONS ARE ASYNC, PUT IN TRY...CATCH

setContributionId: async (contributionId) => {
  try {
    set({ contributionId }, false, 'setContributionId')
  } catch (error) {
    console.error('Failed to set contributionId:', error)
  }
}

*/
const createAppSlice: StateCreator<
  AppStore,
  [['zustand/devtools', never], ['zustand/persist', unknown]]
> = (set, get, store) => ({
  ...initialState,
  setHasHydrated: (value: boolean) =>
    set({ _hasHydrated: value }, false, 'setHasHydrated'),
  setStorageError: (error: string | null) =>
    set({ _storageError: error }, false, 'setStorageError'),

  resetStore: (hydrated: boolean = true) =>
    set(
      Object.assign(store.getInitialState(), {
        _hasHydrated: hydrated,
        _storageError: null,
      }),
      false,
      'resetStore',
    ),

  setGiftInviteCode: (giftInviteCode) =>
    set({ giftInviteCode }, false, 'setGiftInviteCode'),

  // Partial reset: a new contribution wipes everything downstream (message,
  // payment) but keeps the gift context and the contributor's own details —
  // they are the same person, so a back-and-forward through the amount step
  // must not empty the form they already filled in (GIFTWEB-16 AC 10).
  setContributionId: (contributionId) => {
    const {
      amount,
      privateAmount,
      giftName,
      goalType,
      giftImageUrl,
      childFirstName,
      showChildPhoto,
      giftInviteCode,
      legalFirstName,
      legalLastName,
      email,
      phone,
      phoneCountry,
      relationshipToChild,
      nameKnownAs,
      _hasHydrated,
      _storageError,
    } = get();
    set(
      {
        ...initialState,
        _hasHydrated,
        _storageError,
        giftInviteCode,
        amount,
        privateAmount,
        giftName,
        goalType,
        giftImageUrl,
        childFirstName,
        showChildPhoto,
        legalFirstName,
        legalLastName,
        email,
        phone,
        phoneCountry,
        relationshipToChild,
        nameKnownAs,
        contributionId,
      },
      false,
      'setContributionId',
    );
  },

  // senfGift
  setAmount: (amount) => set({ amount }, false, 'setAmount'),
  setPrivateAmount: (privateAmount) =>
    set({ privateAmount }, false, 'setPrivateAmount'),
  setGiftName: (giftName) => set({ giftName }, false, 'setGiftName'),
  setGiftImageUrl: (giftImageUrl) =>
    set({ giftImageUrl }, false, 'setGiftImageUrl'),
  // Set together, always: a URL without its expiry is exactly the state that
  // leaves a dead image on screen with no way to know it is dead.
  setGiftImage: (giftImageUrl, expiresInSeconds) =>
    set(
      {
        giftImageUrl,
        giftImageExpiresAt: expiresInSeconds
          ? Date.now() + expiresInSeconds * 1000
          : 0,
      },
      false,
      'setGiftImage',
    ),
  setChildFirstName: (childFirstName) =>
    set({ childFirstName }, false, 'setChildFirstName'),
  setShowChildPhoto: (showChildPhoto) =>
    set({ showChildPhoto }, false, 'setShowChildPhoto'),
  setGoalType: (goalType) => set({ goalType }, false, 'setGoalType'),

  // personalise occasion
  setOccasion: (occasion) => set({ occasion }, false, 'setOccasion'),
  setMessageType: (messageType) =>
    set({ messageType }, false, 'setMessageType'),
  setMessageData: (messageData) =>
    set({ messageData }, false, 'setMessageData'),
  setMessageDurationSec: (messageDurationSec) =>
    set({ messageDurationSec }, false, 'setMessageDurationSec'),
  setMessageSaved: (messageSaved) =>
    set({ messageSaved }, false, 'setMessageSaved'),
  
  // gift details
  setLegalFirstName: (legalFirstName) =>
    set({ legalFirstName }, false, 'setLegalFirstName'),
  setLegalLastName: (legalLastName) =>
    set({ legalLastName }, false, 'setLegalLastName'),
  setEmail: (email) => set({ email }, false, 'setEmail'),
  setPhone: (phone) => set({ phone }, false, 'setPhone'),
  setRelationshipToChild: (relationshipToChild) =>
    set({ relationshipToChild }, false, 'setRelationshipToChild'),
  setNameKnownAs: (nameKnownAs) =>
    set({ nameKnownAs }, false, 'setNameKnownAs'),
  setContributorImageURL: (contributorImageURL) =>
    set({ contributorImageURL }, false, 'setContributorImageURL'),
  setContributorDetails: (details) =>
    set({ ...details }, false, 'setContributorDetails'),

  setOpenBankingId: (openBankingId) =>
    set({ openBankingId }, false, 'setOpenBankingId'),
  setOpenBankingURL: (openBankingURL) =>
    set({ openBankingURL }, false, 'setOpenBankingURL'),
  // Deliberately narrow: leaving a failed payment behind must not cost the
  // contributor their contribution, their details or the gift they are on.
  clearPaymentAttempt: () =>
    set({ openBankingId: '', openBankingURL: '' }, false, 'clearPaymentAttempt'),
  // Clearing the attempt is what makes "this gift link stays live" true: the
  // landing page would otherwise resume a settled payment forever.
  completeGift: () =>
    set(
      { giftCompleted: true, openBankingId: '', openBankingURL: '' },
      false,
      'completeGift',
    ),
});

//  Store
const safeStorage = {
  getItem: async (name: string) => {
    try {
      return await localForage.getItem<string>(name);
    } catch (error) {
      console.error('Storage read failed:', error);
      return null;
    }
  },
  setItem: async (name: string, value: string) => {
    try {
      await localForage.setItem(name, value);
    } catch (error) {
      console.error('Storage write failed:', error);
      // Optionally notify the user their progress won't be saved
    }
  },
  removeItem: async (name: string) => {
    try {
      await localForage.removeItem(name);
    } catch (error) {
      console.error('Storage remove failed:', error);
    }
  },
};

const useAppStore = create<AppStore>()(
  devtools(
    persist(createAppSlice, {
      name: storeName,
      version: 9,
      migrate: (persistedState: unknown, version: number) => {
        try {
          let state = persistedState as AppStore;
          console.log('incoming state', state, 'version', version);

          switch (true) {
            // Each case upgrades one version and falls through to the next, so a
            // store several versions old is migrated in sequence.
            // @ts-expect-error falls through (noFallthroughCasesInSwitch)
            case version < 1:
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false /* v0 → v1 */,
              };
            // @ts-expect-error falls through
            case version < 2:
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null, // new field in v2
              };
            // @ts-expect-error falls through
            case version < 3:
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null,
              };
            // @ts-expect-error falls through
            case version < 4:
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null,
              };
            // @ts-expect-error falls through
            case version < 5:
              // v5 adds childFirstName / showChildPhoto (GIFTWEB-16); they
              // fall back to initialState and are re-synced on the amount
              // screen.
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null,
              };
            // @ts-expect-error falls through
            case version < 6:
              // v6 adds phoneCountry (GIFTWEB-17); defaults to GB.
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null,
              };
            // @ts-expect-error falls through
            case version < 7: {
              // v7 removes the manual bank transfer fields (GIFTWEB-26): the
              // payment-method picker that reached them is gone, so the keys
              // are dropped rather than left to linger in storage.
              const kept = { ...(state as unknown as Record<string, unknown>) };
              for (const key of [
                'manualBankingId',
                'manualBankingAccountName',
                'manualBankingAccountNumber',
                'manualBankingSortCode',
                'manualBankingReference',
              ]) {
                delete kept[key];
              }
              state = {
                ...initialState,
                ...(kept as unknown as AppStore),
                _hasHydrated: false,
                _storageError: null,
              };
            }
            // @ts-expect-error falls through
            case version < 8:
              // v8 adds giftCompleted (GIFTWEB-30); an older store predates the
              // confirmation screen, so defaulting to false is correct.
              state = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null,
              };
            // @ts-expect-error falls through
            case version < 9:
              // v9 tracks when the signed photo URL expires. An older store has
              // a URL but no expiry, and after this long it is almost certainly
              // dead — 0 reads as expired, so it refreshes once on next load.
              state = {
                ...initialState,
                ...state,
                giftImageExpiresAt: 0,
                _hasHydrated: false,
                _storageError: null,
              };
            // falls through
            default: {
              const finalState = {
                ...initialState,
                ...state,
                _hasHydrated: false,
                _storageError: null,
              };
              console.log('finalState', finalState);
              return finalState as unknown as AppStore;
            }
          }
        } catch (error) {
          console.error(
            'State migration failed, resetting to initial state:',
            error,
          );
          console.log('error return default state', initialState);
          return initialState as unknown as AppStore;
        }
      },
      storage: createJSONStorage(() => safeStorage),
      onRehydrateStorage: () => (state, error) => {
        console.log('Rehydrating store...');
        if (error) {
          console.error('Rehydration failed:', error);
          // Still unblock the app — don't leave users on the spinner forever
          useAppStore.getState().setHasHydrated(true);
          return;
        }
        if (state) {
          console.log('there is state, rehydrating', state);
        }
        state?.setHasHydrated(true);
      },
    }),
    {
      name: storeName,
      store: storeName,
    },
  ),
);

// Hooks (use inside React components)
//
// Prefer these over useAppStore directly — they are pre-scoped selectors
// which prevent unnecessary re-renders.

export const useHydrated = () => useAppStore((s) => s._hasHydrated);

export const useGiftInviteCode = () => useAppStore((s) => s.giftInviteCode);

export const useContributionId = () => useAppStore((s) => s.contributionId);

export const useAmount = () => useAppStore((s) => s.amount);
export const usePrivateAmount = () => useAppStore((s) => s.privateAmount);

export const useGiftName = () => useAppStore((s) => s.giftName);
export const useGoalType = () => useAppStore((s) => s.goalType);
export const useGiftImageUrl = () => useAppStore((s) => s.giftImageUrl);
export const useChildFirstName = () => useAppStore((s) => s.childFirstName);
export const useShowChildPhoto = () => useAppStore((s) => s.showChildPhoto);
export const useOccasion = () => useAppStore((s) => s.occasion);
export const useMessageType = (): MessageType =>
  useAppStore((s) => s.messageType);
export const useMessageData = (): string => useAppStore((s) => s.messageData);
export const useMessageDurationSec = () =>
  useAppStore((s) => s.messageDurationSec);
export const useMessageSaved = () => useAppStore((s) => s.messageSaved);

export const useLegalFirstName = () => useAppStore((s) => s.legalFirstName);
export const useLegalLastName = () => useAppStore((s) => s.legalLastName);
export const useEmail = () => useAppStore((s) => s.email);
export const usePhone = () => useAppStore((s) => s.phone);
export const usePhoneCountry = () => useAppStore((s) => s.phoneCountry);
export const useRelationshipToChild = () =>
  useAppStore((s) => s.relationshipToChild);
export const useNameKnownAs = () => useAppStore((s) => s.nameKnownAs);
export const useContributorImageURL = () =>
  useAppStore((s) => s.contributorImageURL);

export const useOpenBankingId = () => useAppStore((s) => s.openBankingId);
export const useOpenBankingURL = () => useAppStore((s) => s.openBankingURL);


// Individual action hooks
export const useResetStore = () => useAppStore((s) => s.resetStore);
export const useSetGiftInviteCode = () =>
  useAppStore((s) => s.setGiftInviteCode);
export const useSetContributionId = () =>
  useAppStore((s) => s.setContributionId);
export const useSetAmount = () => useAppStore((s) => s.setAmount);
export const useSetPrivateAmount = () =>
  useAppStore((s) => s.setPrivateAmount);
export const useSetGiftName = () => useAppStore((s) => s.setGiftName);
export const useSetGoalType = () => useAppStore((s) => s.setGoalType);
export const useSetOccasion = () => useAppStore((s) => s.setOccasion);
export const useSetOpenBankingId = () => useAppStore((s) => s.setOpenBankingId);
export const useClearPaymentAttempt = () =>
  useAppStore((s) => s.clearPaymentAttempt);
export const useCompleteGift = () => useAppStore((s) => s.completeGift);
export const useGiftCompleted = () => useAppStore((s) => s.giftCompleted);
export const useSetGiftImageUrl = () => useAppStore((s) => s.setGiftImageUrl);
export const useSetGiftImage = () => useAppStore((s) => s.setGiftImage);
export const useGiftImageExpiresAt = () =>
  useAppStore((s) => s.giftImageExpiresAt);
export const useSetChildFirstName = () =>
  useAppStore((s) => s.setChildFirstName);
export const useSetShowChildPhoto = () =>
  useAppStore((s) => s.setShowChildPhoto);
export const useSetMessageType = () => useAppStore((s) => s.setMessageType);
export const useSetMessageData = () => useAppStore((s) => s.setMessageData);
export const useSetMessageDurationSec = () =>
  useAppStore((s) => s.setMessageDurationSec);
export const useSetMessageSaved = () => useAppStore((s) => s.setMessageSaved);
export const useSetLegalFirstName = () =>
  useAppStore((s) => s.setLegalFirstName);
export const useSetLegalLastName = () => useAppStore((s) => s.setLegalLastName);
export const useSetEmail = () => useAppStore((s) => s.setEmail);
export const useSetPhone = () => useAppStore((s) => s.setPhone);
export const useSetRelationshipToChild = () =>
  useAppStore((s) => s.setRelationshipToChild);
export const useSetNameKnownAs = () => useAppStore((s) => s.setNameKnownAs);
export const useSetContributorDetails = () =>
  useAppStore((s) => s.setContributorDetails);
export const useSetContributorImageURL = () =>
  useAppStore((s) => s.setContributorImageURL);
export const useSetOpenBankingURL = () =>
  useAppStore((s) => s.setOpenBankingURL);

// Outside-component access (use in utils, API calls, etc.).
// These read/write state without hooks — safe to call anywhere BUT NOT REACTIVE! They're a single direct read!!

export const getHydrated = () => useAppStore.getState()._hasHydrated;

export const getState = () => useAppStore.getState();

export const getAmount = () => useAppStore.getState().amount;
export const getPrivateAmount = () => useAppStore.getState().privateAmount;
export const getGiftInviteCode = () => useAppStore.getState().giftInviteCode;
export const getContributionId = () => useAppStore.getState().contributionId;
export const getGiftName = () => useAppStore.getState().giftName;
export const getGoalType = () => useAppStore.getState().goalType;
export const getGiftImageUrl = () => useAppStore.getState().giftImageUrl;
export const getChildFirstName = () => useAppStore.getState().childFirstName;
export const getShowChildPhoto = () => useAppStore.getState().showChildPhoto;
export const getOccasion = () => useAppStore.getState().occasion;
export const getMessageType = () => useAppStore.getState().messageType;
export const getMessageDurationSec = () =>
  useAppStore.getState().messageDurationSec;
export const getMessageSaved = () => useAppStore.getState().messageSaved;
export const getMessageData = () => useAppStore.getState().messageData;
export const getLegalFirstName = () => useAppStore.getState().legalFirstName;
export const getLegalLastName = () => useAppStore.getState().legalLastName;
export const getEmail = () => useAppStore.getState().email;
export const getPhone = () => useAppStore.getState().phone;
export const getPhoneCountry = () => useAppStore.getState().phoneCountry;
export const getRelationshipToChild = () =>
  useAppStore.getState().relationshipToChild;
export const getNameKnownAs = () => useAppStore.getState().nameKnownAs;
export const getContributorImageURL = () =>
  useAppStore.getState().contributorImageURL;
export const getOpenBankingId = () => useAppStore.getState().openBankingId;
export const getOpenBankingURL = () => useAppStore.getState().openBankingURL;


export type { MessageType };

// Extend the Window interface
declare global {
  interface Window {
    useAppStore: typeof useAppStore;
  }
}

// Attach to window (only in development)
if (import.meta.env.DEV) {
  window.useAppStore = useAppStore;
  console.log('useAppStore attached to window:', window.useAppStore);
}
// for debugging

export default useAppStore;
